import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Stack, Tab, Tabs, Typography } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import { sanitizeHtml } from '../../lib/format';

export interface OpenFileDetail {
  url: string;
  name?: string;
  /** When given, the viewer shows a Delete button that calls this (used by the editor). */
  onDelete?: () => void;
}

const EVENT = 'pma:open-file';
export const openFile = (detail: OpenFileDetail) => window.dispatchEvent(new CustomEvent<OpenFileDetail>(EVENT, { detail }));

const extOf = (url: string, name?: string) => {
  const source = (name && /\.[a-z0-9]+$/i.test(name) ? name : url).split('?')[0];
  const m = source.match(/\.([a-z0-9]+)$/i);
  return m ? m[1].toLowerCase() : '';
};
const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif'];
const TEXT_EXT = ['txt', 'csv', 'json', 'md', 'log', 'xml', 'yml', 'yaml', 'ini', 'js', 'ts', 'css', 'html'];
export const isImageUrl = (url: string, name?: string) => IMAGE_EXT.includes(extOf(url, name));

/** Opens in-app (no new tab) for anything we can render; other formats only offer a download. */
export const isViewableUrl = (url: string, name?: string) => {
  const e = extOf(url, name);
  return IMAGE_EXT.includes(e) || TEXT_EXT.includes(e) || ['pdf', 'docx', 'xlsx', 'xls'].includes(e);
};

/** Matches links that point at files uploaded through the PMA editor. */
export const isUploadedFileUrl = (url: string) => /\/uploads\//.test(url);

/** Files live on a CDN without CORS headers, so the reader/download go through the API's same-origin proxy. */
async function fetchBlob(url: string) {
  const res = await api.get<Blob>('/uploads/proxy', { params: { url }, responseType: 'blob' });
  return res.data;
}

export async function downloadFile(url: string, name?: string) {
  const blob = await fetchBlob(url);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = name || url.split('/').pop() || 'file';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

type Content =
  | { kind: 'loading' }
  | { kind: 'image' }
  | { kind: 'pdf'; src: string }
  | { kind: 'text'; text: string }
  | { kind: 'html'; html: string }
  | { kind: 'sheets'; sheets: { name: string; html: string }[] }
  | { kind: 'unsupported' }
  | { kind: 'error'; message: string };

function Reader({ file }: { file: OpenFileDetail }) {
  const [content, setContent] = useState<Content>({ kind: 'loading' });
  const [sheet, setSheet] = useState(0);
  const blobUrl = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const ext = extOf(file.url, file.name);
    setContent({ kind: 'loading' });
    setSheet(0);

    (async () => {
      try {
        if (IMAGE_EXT.includes(ext)) return setContent({ kind: 'image' });
        if (ext === 'pdf') {
          const blob = await fetchBlob(file.url);
          const src = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
          blobUrl.current = src;
          return !cancelled && setContent({ kind: 'pdf', src });
        }
        if (TEXT_EXT.includes(ext)) {
          const blob = await fetchBlob(file.url);
          return !cancelled && setContent({ kind: 'text', text: await blob.text() });
        }
        if (ext === 'docx') {
          const buffer = await (await fetchBlob(file.url)).arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
          return !cancelled && setContent({ kind: 'html', html: sanitizeHtml(result.value) });
        }
        if (ext === 'xlsx' || ext === 'xls') {
          const buffer = await (await fetchBlob(file.url)).arrayBuffer();
          const wb = XLSX.read(buffer, { type: 'array' });
          const sheets = wb.SheetNames.map((name) => ({ name, html: sanitizeHtml(XLSX.utils.sheet_to_html(wb.Sheets[name])) }));
          return !cancelled && setContent({ kind: 'sheets', sheets });
        }
        if (!cancelled) setContent({ kind: 'unsupported' });
      } catch {
        if (!cancelled) setContent({ kind: 'error', message: 'Could not load this file.' });
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrl.current) URL.revokeObjectURL(blobUrl.current);
      blobUrl.current = null;
    };
  }, [file.url, file.name]);

  const box = { width: '100%', height: '72vh', border: 0 } as const;
  switch (content.kind) {
    case 'loading':
      return <Typography sx={{ py: 6, textAlign: 'center' }} color="text.secondary">Loading…</Typography>;
    case 'image':
      return <Box sx={{ textAlign: 'center' }}><Box component="img" src={file.url} alt={file.name || ''} sx={{ maxWidth: '100%', maxHeight: '72vh', objectFit: 'contain' }} /></Box>;
    case 'pdf':
      return <iframe title={file.name || 'PDF'} src={content.src} style={box} />;
    case 'text':
      return <Box component="pre" sx={{ m: 0, p: 2, bgcolor: 'action.hover', maxHeight: '72vh', overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 13 }}>{content.text}</Box>;
    case 'html':
      return <Box sx={{ maxHeight: '72vh', overflow: 'auto', p: 1, '& img': { maxWidth: '100%' } }} dangerouslySetInnerHTML={{ __html: content.html }} />;
    case 'sheets':
      return (
        <Box>
          {content.sheets.length > 1 && (
            <Tabs value={sheet} onChange={(_e, v) => setSheet(v)} variant="scrollable" sx={{ mb: 1 }}>
              {content.sheets.map((s) => <Tab key={s.name} label={s.name} />)}
            </Tabs>
          )}
          <Box
            sx={{ maxHeight: '68vh', overflow: 'auto', '& table': { borderCollapse: 'collapse', fontSize: 13 }, '& td, & th': { border: '1px solid', borderColor: 'divider', px: 1, py: 0.5 } }}
            dangerouslySetInnerHTML={{ __html: content.sheets[sheet]?.html ?? '' }}
          />
        </Box>
      );
    case 'unsupported':
      return <Typography sx={{ py: 6, textAlign: 'center' }} color="text.secondary">This file type can&apos;t be previewed here. Use Download to open it.</Typography>;
    default:
      return <Typography sx={{ py: 6, textAlign: 'center' }} color="error">{content.message}</Typography>;
  }
}

/** Mount once (in PmaApp). Shows images in a lightbox and documents in an in-app reader. */
export function FileViewerHost() {
  const [file, setFile] = useState<OpenFileDetail | null>(null);

  useEffect(() => {
    const handler = (e: Event) => setFile((e as CustomEvent<OpenFileDetail>).detail);
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);

  if (!file) return null;
  const close = () => setFile(null);
  return (
    <Dialog open onClose={close} maxWidth="lg" fullWidth sx={{ zIndex: 1600 }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography component="span" sx={{ flex: 1, fontWeight: 800 }} noWrap>{file.name || file.url.split('/').pop()}</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<DownloadRounded />} onClick={() => downloadFile(file.url, file.name)}>Download</Button>
          {file.onDelete && (
            <Button size="small" color="error" variant="outlined" startIcon={<DeleteOutlineRounded />} onClick={() => { file.onDelete?.(); close(); }}>Delete</Button>
          )}
          <IconButton size="small" onClick={close}><CloseRounded /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers><Reader file={file} /></DialogContent>
    </Dialog>
  );
}
